/**
 * UI audio cues for the human-call talk handoff (BI-982).
 *
 * During a multi-attach call the server grants the mic to one attachment at a
 * time. When the human is granted the mic they should hear a short rising tone
 * ("you may speak"); when an agent takes over and the human is revoked they
 * hear a falling tone ("an agent has the mic"). These are pure UI feedback —
 * they are NOT injected into the call audio, so they carry no PHI.
 *
 * Tones are synthesized with the Web Audio API (no asset files, no network). A
 * single AudioContext is reused across cues; it is created lazily on first use,
 * by which point a call is already in progress so the context is unlocked.
 *
 * Every entry point is wrapped so a cue failure can never disrupt an active
 * call — at worst the user just doesn't hear the beep.
 */

/** A two-tone cue: play `from` Hz then `to` Hz, each for `toneMs`. */
interface CueShape {
  from: number;
  to: number;
}

const GRANT_CUE: CueShape = { from: 660, to: 990 }; // rising — "you may speak"
const REVOKE_CUE: CueShape = { from: 990, to: 660 }; // falling — "agent took over"

/** Duration of each of the two tones, in milliseconds. */
const TONE_MS = 150;
/** Peak gain; kept low so the cue is noticeable but not jarring. */
const PEAK_GAIN = 0.15;
/** Linear ramp at each tone edge to avoid audible clicks, in seconds. */
const RAMP_S = 0.01;

let sharedContext: AudioContext | null = null;

/** Lazily create (and resume) the shared AudioContext. Returns null if Web Audio is unavailable. */
function getContext(): AudioContext | null {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedContext) {
    sharedContext = new Ctor();
  }
  // A call is in progress when cues fire, but resume() is cheap insurance
  // against a context that drifted into the "suspended" state.
  if (sharedContext.state === 'suspended') {
    void sharedContext.resume();
  }
  return sharedContext;
}

/** Schedule one tone on `ctx` starting at `startTime`, with click-free edges. */
function scheduleTone(
  ctx: AudioContext,
  frequency: number,
  startTime: number,
  durationS: number,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequency, startTime);

  // Trapezoidal envelope: ramp up, hold, ramp down.
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(PEAK_GAIN, startTime + RAMP_S);
  gain.gain.setValueAtTime(PEAK_GAIN, startTime + durationS - RAMP_S);
  gain.gain.linearRampToValueAtTime(0, startTime + durationS);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + durationS);
}

/** Play a two-tone cue. Never throws. */
function playCue(cue: CueShape): void {
  try {
    const ctx = getContext();
    if (!ctx) return;
    const toneS = TONE_MS / 1000;
    const start = ctx.currentTime;
    scheduleTone(ctx, cue.from, start, toneS);
    scheduleTone(ctx, cue.to, start + toneS, toneS);
  } catch {
    // Audio cues are best-effort; swallow any Web Audio error.
  }
}

/** Rising cue: the human has been granted the mic and may speak. */
export function playGrantCue(): void {
  playCue(GRANT_CUE);
}

/** Falling cue: the human's mic was revoked (an agent has taken over). */
export function playRevokeCue(): void {
  playCue(REVOKE_CUE);
}

// ---------------------------------------------------------------------------
// Ringback cue (BI-982)
//
// A looping ring played while a proxied call is dialing — from just after
// AckCall until the first audio arrives from the caller (i.e. they answered).
// Unlike the one-shot grant/revoke cues this repeats on a cadence, so it owns a
// timer and tracks its live oscillators to stop cleanly mid-tone.
// ---------------------------------------------------------------------------

/** Classic North-American ringback: 440 Hz + 480 Hz played together. */
const RINGBACK_FREQS = [440, 480];
/** Tone-on duration per ring, in seconds. */
const RINGBACK_ON_S = 2;
/** Full cadence period (2 s on + 4 s off), in milliseconds. */
const RINGBACK_PERIOD_MS = 6000;
/** Per-oscillator gain; the two sum, so kept low to avoid a harsh/clipped ring. */
const RINGBACK_GAIN = 0.08;

let ringbackTimer: ReturnType<typeof setInterval> | null = null;
let ringbackOscs: OscillatorNode[] = [];

/** Schedule one ring burst (both tones for RINGBACK_ON_S), tracking the oscillators. */
function scheduleRingbackBurst(ctx: AudioContext): void {
  const start = ctx.currentTime;
  for (const freq of RINGBACK_FREQS) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, start);
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(RINGBACK_GAIN, start + RAMP_S);
    gain.gain.setValueAtTime(RINGBACK_GAIN, start + RINGBACK_ON_S - RAMP_S);
    gain.gain.linearRampToValueAtTime(0, start + RINGBACK_ON_S);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + RINGBACK_ON_S);
    ringbackOscs.push(osc);
  }
}

/**
 * Start the looping ringback. Idempotent — a second call while ringing is a
 * no-op. Never throws; if Web Audio is unavailable the caller simply hears
 * nothing.
 */
export function startRingbackCue(): void {
  try {
    if (ringbackTimer) return; // already ringing
    const ctx = getContext();
    if (!ctx) return;
    scheduleRingbackBurst(ctx); // ring immediately, then on the cadence
    ringbackTimer = setInterval(() => {
      try {
        scheduleRingbackBurst(ctx);
      } catch {
        // best-effort; a failed burst must not break the timer's contract
      }
    }, RINGBACK_PERIOD_MS);
  } catch {
    // Audio cues are best-effort; swallow any Web Audio error.
  }
}

/** Stop the ringback and silence any tone still playing. Idempotent. */
export function stopRingbackCue(): void {
  if (ringbackTimer) {
    clearInterval(ringbackTimer);
    ringbackTimer = null;
  }
  for (const osc of ringbackOscs) {
    try {
      osc.stop();
    } catch {
      // already stopped / context gone — ignore
    }
  }
  ringbackOscs = [];
}

/** Release the shared AudioContext. Exposed for tests and teardown. */
export function closeCueAudio(): void {
  stopRingbackCue();
  try {
    void sharedContext?.close();
  } catch {
    // ignore
  }
  sharedContext = null;
}
